import React from 'react';
import {
  Package,
  Wrench,
  ArrowRightLeft,
  Receipt,
  User,
  Building2,
} from 'lucide-react';
import { useTranslation } from '@hooks/useTranslation';

interface BudgetSummaryHeaderProps {
  totalMaterialsCost: number | null;
  totalEquipmentsCost: number | null;
  totalDelegatedCost: number | null;
  totalLineItemsCost: number | null;
  totalAmount: number;
  projectBudget: number | null;
  isDelegated: boolean;
  materialCount: number;
  equipmentCount: number;
  delegatedCount: number;
  expenseCount: number;
  projectState?: string;
  originOrgName?: string;
  clientName?: string;
  pmName?: string;
  superintendentName?: string;
}

// Sprint 27, story-138. Made null-safe: a redacted/hidden total (e.g. a REVIEWER/CLIENT's
// money-posture redaction, or a non-own materials row whose cost basis is now correctly hidden
// per story-138's fix) must render as "not shown", never a crash on `null.toLocaleString()` and
// never a false `$0.00`/`$ NaN` — same convention as `InventoryItemCard`'s `formatCurrency`
// (story-136).
const formatCurrency = (value?: number | null) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '-';
  }
  return `$ ${value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

export const BudgetSummaryHeader: React.FC<BudgetSummaryHeaderProps> = ({
  totalMaterialsCost,
  totalEquipmentsCost,
  totalDelegatedCost,
  totalLineItemsCost,
  totalAmount,
  projectBudget,
  isDelegated,
  materialCount,
  equipmentCount,
  delegatedCount,
  expenseCount,
  projectState,
  originOrgName,
  clientName,
  pmName,
  superintendentName,
}) => {
  const tProjectComponents = useTranslation('projectComponents');
  const t = tProjectComponents.budgetSummaryHeader;
  const isOverBudget = projectBudget ? totalAmount > projectBudget : false;
  const shouldShowBudgetInfo = !isDelegated && !!projectBudget && projectBudget > 0;
  const percentageSpent = projectBudget
    ? ((totalAmount / projectBudget) * 100).toFixed(2)
    : '0.00';

  return (
    <div className="bg-gray-50 rounded-lg px-4 py-3 mb-3">
      {/* Top row: badges + counts */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {shouldShowBudgetInfo && (
            <div
              className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                !isOverBudget
                  ? 'text-green-700 bg-green-100'
                  : 'text-red-700 bg-red-100'
              }`}>
              {!isOverBudget ? t.onBudget : t.overBudget}
            </div>
          )}
          <div
            className={`text-xs font-medium px-2.5 py-1 rounded-full ${
              isDelegated
                ? 'text-orange-700 bg-orange-100'
                : 'text-blue-700 bg-blue-100'
            }`}>
            <span className="flex items-center gap-1">
              {isDelegated && <ArrowRightLeft size={12} />}
              {isDelegated ? t.delegated : t.owned}
            </span>
          </div>
          {projectState && (
            <span className="text-xs text-gray-500 font-medium">
              {projectState}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1 text-xs text-gray-600">
            <Package size={13} className="text-gray-400" />
            {t.materialsCount.replace('{n}', String(materialCount))}
          </span>
          <span className="flex items-center gap-1 text-xs text-gray-600">
            <Wrench size={13} className="text-gray-400" />
            {t.equipmentCount.replace('{n}', String(equipmentCount))}
          </span>
          {delegatedCount > 0 && (
            <span className="flex items-center gap-1 text-xs text-gray-600">
              <ArrowRightLeft size={13} className="text-gray-400" />
              {t.delegatedCount.replace('{n}', String(delegatedCount))}
            </span>
          )}
          <span className="flex items-center gap-1 text-xs text-gray-600">
            <Receipt size={13} className="text-gray-400" />
            {t.lineItemsCount.replace('{n}', String(expenseCount))}
          </span>
        </div>
      </div>

      {/* Category cost cards */}
      <div className="grid grid-cols-4 gap-2 mt-2">
        <div className="bg-white rounded-md px-3 py-2 border border-gray-100">
          <p className="text-[10px] text-gray-500 uppercase">{t.materials}</p>
          <p className="text-sm font-semibold text-gray-800">
            {formatCurrency(totalMaterialsCost)}
          </p>
        </div>
        <div className="bg-white rounded-md px-3 py-2 border border-gray-100">
          <p className="text-[10px] text-gray-500 uppercase">{t.equipment}</p>
          <p className="text-sm font-semibold text-gray-800">
            {formatCurrency(totalEquipmentsCost)}
          </p>
        </div>
        <div className="bg-white rounded-md px-3 py-2 border border-gray-100">
          <p className="text-[10px] text-gray-500 uppercase">{t.delegatedLabel}</p>
          <p className="text-sm font-semibold text-gray-800">
            {formatCurrency(totalDelegatedCost)}
          </p>
        </div>
        <div className="bg-white rounded-md px-3 py-2 border border-gray-100">
          <p className="text-[10px] text-gray-500 uppercase">{t.lineItems}</p>
          <p className="text-sm font-semibold text-gray-800">
            {formatCurrency(totalLineItemsCost)}
          </p>
        </div>
      </div>

      {/* Context details row */}
      <div className="flex items-center gap-4 mt-2 text-xs text-gray-600">
        {isDelegated && originOrgName && (
          <span className="flex items-center gap-1">
            <Building2 size={13} className="text-gray-400" />
            {t.from}{' '}
            <span className="font-medium text-gray-800">{originOrgName}</span>
          </span>
        )}
        {clientName && (
          <span className="flex items-center gap-1">
            <Building2 size={13} className="text-gray-400" />
            {t.client}{' '}
            <span className="font-medium text-gray-800">{clientName}</span>
          </span>
        )}
        {pmName && (
          <span className="flex items-center gap-1">
            <User size={13} className="text-gray-400" />
            {t.pm} <span className="font-medium text-gray-800">{pmName}</span>
          </span>
        )}
        {superintendentName && (
          <span className="flex items-center gap-1">
            <User size={13} className="text-gray-400" />
            {t.supt}{' '}
            <span className="font-medium text-gray-800">
              {superintendentName}
            </span>
          </span>
        )}
        {shouldShowBudgetInfo && (
          <span className="ml-auto text-xs">
            {t.budget}{' '}
            <span className="font-semibold text-gray-800">
              {formatCurrency(projectBudget || 0)}
            </span>
            <span className="mx-1">|</span>
            {t.spent}{' '}
            <span
              className={`font-semibold ${isOverBudget ? 'text-red-600' : 'text-green-600'}`}>
              {formatCurrency(totalAmount)}
            </span>
            <span className="mx-1">|</span>
            <span
              className={`font-semibold ${isOverBudget ? 'text-red-600' : 'text-green-600'}`}>
              {percentageSpent}%
            </span>
          </span>
        )}
      </div>
    </div>
  );
};
